import React, { useEffect, useRef, useState } from 'react';
import Countries from '../assets/countriesJSON/countries.json';
import { useNavigate } from "react-router-dom";
import { FaBuilding } from 'react-icons/fa';
import ReactGooglePlacesAutocomplete from 'react-google-places-autocomplete';

const baseUrl = process.env.REACT_APP_API_URL;

export function List() {
  const auth = localStorage.getItem("auth");
  const authJsonObject = JSON.parse(auth);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [responseMessage, setResponseMessage] = useState(''); 
  const navigate = useNavigate();
  const [userType, setUserType] = useState('company'); 
  const [showPasswordPopup, setShowPasswordPopup] = useState(false); 
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(''); 
  const [passwordSuccess, setPasswordSuccess] = useState(''); 
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phonenumber: '',
    company: '',
    companywebsite: '',   
    address: '',
    city: '',
    state: '',
    country: '',
    ZIPCode: '',
    geometryfile: '',
    companydetails: '',
    company_logo: '' ,
    provider_type: 'company',
     
  });
  const [originalData, setOriginalData] = useState(formData);
  const [logoSrc, setLogoSrc] = useState(null); 
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // NEW: Track if logo was changed
  const [logoChanged, setLogoChanged] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          containerRef.current && !containerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const token = authJsonObject["token"];
        const response = await fetch(`${baseUrl}/currentuser`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }); 

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (isMounted) {
          const profileData = result.profile;
          const data = {
            firstname: profileData.firstname || '',
            lastname: profileData.lastname || '',
            email: profileData.email || '',
            phonenumber: profileData.phonenumber || '',
            company: profileData.company || '',
            companywebsite: profileData.companywebsite || '', 
            companydetails: profileData.companydetails || '',
            address: profileData.address || '',
            city: profileData.city || '',
            state: profileData.state || '',
            country: profileData.country || '',
            ZIPCode: profileData.ZIPCode || '',
            geometryfile: profileData.geometryfile || '',
            company_logo: profileData.company_logo || '',
            provider_type: profileData.provider_type || 'company' 
          };
          setProfile(profileData);
          setFormData(data);
          setOriginalData(data);
          setLogoSrc(profileData.company_logo ? `data:image/png;base64,${profileData.company_logo}` : null);
          setUserType(profileData.provider_type || 'company');
          setLogoChanged(false); // NEW: Reset logo changed flag

          const editableFields = ['firstname', 'lastname', 'phonenumber', 'company', 'companywebsite', 'address', 'city', 'state', 'country', 'ZIPCode', 'companydetails'];
          const allEmpty = editableFields.every(field => !data[field]);
          setIsEditing(allEmpty);
        }
      } catch (error) {
        setError(error.message);
        console.error('Error fetching user profile:', error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value || '',
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!validImageTypes.includes(file.type)) {
        setResponseMessage('Please upload a valid image file (JPEG or PNG)');
        return;
      }
  
      setFormData((prevState) => ({
        ...prevState,
        company_logo: file,
      }));
  
      const fileUrl = URL.createObjectURL(file);
      setLogoSrc(fileUrl);
      setLogoChanged(true); // NEW: Mark that logo was changed
    }
  };

  const handleUserTypeChange = (e) => {
    const userTypeValue = e.target.value;
    setUserType(userTypeValue);
    setFormData((prevState) => ({
      ...prevState,
      provider_type: userTypeValue,
    }));
  };
  
  const saveUserDetails = async () => {
    const requiredFields = ['firstname', 'lastname', 'phonenumber', 'address', 'city', 'state', 'country', 'ZIPCode'];
  
    const emptyFields = requiredFields.filter(field => !formData[field]);
  
    if (emptyFields.length > 0) {
      setResponseMessage('The following fields are required: ' + emptyFields.join(', '));
      return;
    }
  
    try {
      const token = authJsonObject["token"];
      const formDataObj = new FormData();
  
      Object.keys(formData).forEach((key) => {
        // FIX: Only send company_logo if it was changed AND it's a File object
        if (key === 'company_logo') {
          if (logoChanged && formData[key] instanceof File) {
            // This is a new file upload - send it
            formDataObj.append('company_logo', formData[key]);
          }
          // If logoChanged is false, don't send logo at all
          // This prevents sending the huge base64 string
        } else if (key === 'company' && (!formData[key] || formData[key].trim() === '')) {
          formDataObj.append(key, 'N/A');
        } else if (key === 'companywebsite' && (!formData[key] || formData[key].trim() === '')) {
          formDataObj.append(key, 'N/A');
        } else if (key === 'companydetails' && (!formData[key] || formData[key].trim() === '')) {
          formDataObj.append(key, 'N/A');
        } else {
          formDataObj.append(key, formData[key] || '');
        }
      });
  
      const response = await fetch(`${baseUrl}/update_currentuser`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataObj,
      });
  
      if (!response.ok) {
        const errorDetail = await response.text();
        if (response.status === 400 && errorDetail.includes('No changes made to the user details')) {
          navigate('/termsofuse');
          return;
        }        
        throw new Error(`HTTP error! status: ${response.status}, details: ${errorDetail}`);
      }
  
      const updatedFormData = { ...formData };
      if (!formData.company || formData.company.trim() === '') {
        updatedFormData.company = 'N/A';
      }
      if (!formData.companywebsite || formData.companywebsite.trim() === '') {
        updatedFormData.companywebsite = 'N/A';
      }
      if (!formData.companydetails || formData.companydetails.trim() === '') {
        updatedFormData.companydetails = 'N/A';
      }
      
      setProfile(updatedFormData);
      setOriginalData(updatedFormData);
      setIsEditing(false);
      setLogoChanged(false); // NEW: Reset logo changed flag
  
      setResponseMessage('User details updated successfully.');
      navigate('/termsofuse');
  
    } catch (error) {
      setResponseMessage(`Error updating user details: ${error.message}`);
      console.error('Error updating user details:', error);
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    setLogoChanged(false); // NEW: Reset logo changed flag when starting edit
  };

  const cancelEditing = () => {
    setFormData(originalData);
    setIsEditing(false);
    setLogoChanged(false); // NEW: Reset logo changed flag when canceling
    // Reset logo display
    if (originalData.company_logo) {
      setLogoSrc(`data:image/png;base64,${originalData.company_logo}`);
    } else {
      setLogoSrc(null);
    }
  };

  const editableFields = ['firstname', 'lastname', 'phonenumber', 'company', 'companywebsite', 'address', 'city', 'state', 'country', 'ZIPCode', 'companydetails'];
  const allEditableFieldsEmpty = editableFields.every(field => !formData[field]);

  const openPasswordPopup = () => {
    setShowPasswordPopup(true);
    setPasswordError('');
    setPasswordSuccess('');
  };

  const closePasswordPopup = () => {
    setShowPasswordPopup(false);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
  
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
  
    try {
      const token = authJsonObject["token"];
      const response = await fetch(`${baseUrl}/change_password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      setPasswordSuccess('Your password has been successfully changed.');
  
      setTimeout(() => {
        closePasswordPopup();
      }, 2000);
  
    } catch (error) {
      setPasswordError('Error changing password: ' + error.message);
    }
  };  
  
  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="user-profile-container">
      <h1>Provider Profile Details</h1>
      <p>Please ensure this information is accurate, as it will be visible to the customers.</p>
      <div className="button-container">
        {profile && !isEditing && (
          <button
            onClick={startEditing}
            className="btn btn-primary me-2"
          >
            Edit User Details
          </button>
        )}
        {!["Google", "LinkedIn", "Microsoft"].includes(profile?.user_type) && (
          <button onClick={openPasswordPopup} className="btn btn-primary">
            Change Password
          </button>
        )}
      </div>
      {responseMessage && (
        <div className="alert alert-info" role="alert">
          {responseMessage}
        </div>
      )}

      <div className="radio-buttons">
        <div>
          <label className="radio-button-label">
            <input
              type="radio"
              name="userType"
              value="individual"
              checked={userType === 'individual'}
              onChange={handleUserTypeChange}
            />
            Are you Individual?
          </label>
        </div>
        <div>
          <label className="radio-button-label">
            <input
              type="radio"
              name="userType"
              value="company"
              checked={userType === 'company'}
              onChange={handleUserTypeChange}
            />
            Are you Company?
          </label>
        </div>
      </div>

      {profile ? (
        <table className="table table-striped table-mobile-responsive">
          <tbody>
            <tr>
              <td style={{width: '100%'}}>Company Logo</td>
              <td style={{width: '100%', height: '220px'}}>
                <div className="logo-container">
                  {isEditing ? (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px dashed #ccc',
                          borderRadius: '8px',
                          padding: '20px',
                          width: '100%',
                          aspectRatio: '1 / 1',
                          backgroundColor: '#f9f9f9'
                        }}
                      >
                        {logoSrc ? (
                          <img 
                            src={logoSrc} 
                            alt="Company Logo"
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}  
                          />
                        ) : (
                          <FaBuilding size={48} color="#ccc" />
                        )}
                      </div>
                      <input
                        type="file"
                        name="company_logo"
                        onChange={handleFileChange}
                        className="file-input"
                        accept=".jpg,.jpeg,.png"
                        style={{
                          marginTop: '12px',
                          fontSize: '14px',
                          width: '100%',
                          cursor: 'pointer'
                        }}
                      />
                    </>
                  ) : (
                    logoSrc ? (
                      <img 
                        src={logoSrc} 
                        alt="Company Logo"  
                        style={{ 
                          width: '100%', 
                          maxWidth: '150px',
                          height: 'auto',
                          borderRadius: '4px' 
                        }} 
                      />
                    ) : (
                      <FaBuilding style={{ fontSize: '60px', color: '#ccc' }} />
                    )
                  )}
                </div>
              </td>
            </tr>

            <tr>
              <td style={{width: '100%'}}>{userType === 'company' ? 'Company Name' : 'Pilot Name'} (Optional)</td>
              <td style={{width: '100%'}}>
                {isEditing ? (
                  <input
                    type="text"
                    name="company"
                    value={formData.company === 'N/A' ? '' : formData.company || ''}
                    onChange={handleChange}
                    placeholder="Optional - Enter company name"
                  />
                ) : (
                  profile.company || 'N/A'
                )}
              </td>
            </tr>

            <tr>
              <td style={{width: '100%'}}>Website (Optional)</td>
              <td style={{width: '100%'}}>
                {isEditing ? (
                  <input
                    type="text"
                    name="companywebsite"
                    value={formData.companywebsite === 'N/A' ? '' : formData.companywebsite || ''}
                    onChange={handleChange}
                    placeholder="Optional - Enter website URL"
                  />
                ) : (
                  formData.companywebsite && formData.companywebsite !== 'N/A' ? (
                    <a
                      href={formData.companywebsite.startsWith('http') ? formData.companywebsite : `http://${formData.companywebsite}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {formData.companywebsite}
                    </a>
                  ) : (
                    'N/A'
                  )
                )}
              </td>
            </tr>

            <tr>
              <td style={{width: '100%'}}>{userType === 'company' ? 'Company Details' : 'Pilot Details'} (Optional)</td>
              <td style={{width: '100%'}}>
                {isEditing ? (
                  <textarea
                    name="companydetails"
                    value={formData.companydetails === 'N/A' ? '' : formData.companydetails || ''}
                    onChange={handleChange}
                    placeholder="Optional - What are the most important things you want customers to understand about your company?" 
                    style={{
                      width: '100%',
                      height: '100px',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxSizing: 'border-box',
                      resize: 'none',
                      transition: 'border-color 0.3s',
                      backgroundColor: 'transparent',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      backgroundColor: '#f9f9f9',
                      padding: '10px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      width: '100%',
                      height: '100px',
                      overflowY: 'auto',
                    }}
                  >
                    {profile.companydetails || 'N/A'}
                  </div>
                )}
              </td>
            </tr>

            <tr>
              <td style={{width: '100%'}}>Email</td>
              <td style={{width: '100%'}}>
                {profile.email}
              </td>
            </tr>

            <tr>
              <td style={{width: '100%'}}>Contact First Name</td>
              <td style={{width: '100%'}}>
                {isEditing ? (
                  <input
                    type="text"
                    name="firstname"
                    value={formData.firstname || ''}
                    onChange={handleChange}
                  />
                ) : (
                  profile.firstname || 'N/A'
                )}
              </td>
            </tr>

            <tr>
              <td style={{width: '100%'}}>Contact Last Name</td>
              <td style={{width: '100%'}}>
                {isEditing ? (
                  <input
                    type="text"
                    name="lastname"
                    value={formData.lastname || ''}
                    onChange={handleChange}
                  />
                ) : (
                  profile.lastname || 'N/A'
                )}
              </td>
            </tr>

            <tr>
              <td style={{width: '100%'}}>Phone Number</td>
              <td style={{width: '100%'}}>
                {isEditing ? (
                  <input
                    type="text"
                    name="phonenumber"
                    value={formData.phonenumber || ''}
                    onChange={handleChange}
                  />
                ) : (
                  profile.phonenumber || 'N/A'
                )}
              </td>
            </tr>

            <tr>
              <td style={{width: '100%'}}>Address</td>
              <td style={{width: '100%'}}>
                {isEditing ? (
                  <input
                    type="text"
                    name="address"
                    value={formData.address || ''}
                    onChange={handleChange}
                  />
                ) : (
                  profile.address || 'N/A'
                )}
              </td>
            </tr>

            <tr>
              <td style={{ width: '100%' }}>City</td>
              <td style={{ width: '100%', position: 'relative' }} ref={containerRef}>
                {isEditing ? (
                  <>
                    <div style={{ position: 'relative', zIndex: 1 }}>
                      <ReactGooglePlacesAutocomplete
                        apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                        selectProps={{
                          value: formData.city ? { label: formData.city, value: formData.city } : null,
                          onChange: (value) => {
                            setFormData((prevState) => ({
                              ...prevState,
                              city: value ? value.label : '',
                            }));
                            setIsDropdownOpen(false);
                          },
                          onMenuOpen: () => setIsDropdownOpen(true),
                          onMenuClose: () => setIsDropdownOpen(false),
                          menuIsOpen: isDropdownOpen,
                          styles: {
                            menu: (provided) => ({
                              ...provided,
                              position: 'absolute',
                              zIndex: 9999,
                              width: '100%',
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          },
                          menuPortalTarget: document.body,
                          menuPosition: 'fixed',
                        }}
                      />
                    </div>
                  </>
                ) : (
                  profile.city || 'N/A'
                )}
              </td>
            </tr>

            <tr>
              <td style={{width: '100%'}}>State / Province</td>
              <td style={{width: '100%'}}>
                {isEditing ? (
                  <input
                    type="text"
                    name="state"
                    value={formData.state || ''}
                    onChange={handleChange}
                  />
                ) : (
                  profile.state || 'N/A'
                )}
              </td>
            </tr>

            <tr>
              <td style={{width: '100%'}}>ZIP Code</td>
              <td style={{width: '100%'}}>
                {isEditing ? (
                  <input
                    type="text"
                    name="ZIPCode"
                    value={formData.ZIPCode || ''}
                    onChange={handleChange}
                  />
                ) : (
                  profile.ZIPCode || 'N/A'
                )}
              </td>
            </tr>

            <tr>
              <td style={{width: '100%'}}>Country</td>
              <td style={{width: '100%'}}>
                {isEditing ? (
                  <select
                    name="country"
                    value={formData.country || ''}
                    onChange={handleChange}
                  >
                    <option value="">Select Country</option>
                    {Object.entries(Countries).map(([key, value], index) => (
                      <option key={index} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                ) : (
                  profile.country || 'N/A'
                )}
              </td>
            </tr>
            
            <tr>
              <td style={{width: '100%'}}>Service Area KML File</td>
              <td style={{width: '100%'}}>
                <a href={profile.geometryfile} download="geometry_file.kml" target="_blank" rel="noopener noreferrer">
                  {profile.geometryfile}
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <div>Loading...</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: '30px'}}>
        <button
          onClick={saveUserDetails}
          className="btn btn-primary"
          disabled={!logoSrc && allEditableFieldsEmpty}
        >
          Go to Step 5 <br />
          (Terms and Conditions)
        </button>
      </div>

      {showPasswordPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h2>Change Password</h2>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="form-control"
                />
              </div>
              {passwordError && <p className="error-text">{passwordError}</p>}
              {passwordSuccess && <p className="success-text">{passwordSuccess}</p>}
              <div className="button-group">
                <button type="submit" className="btn btn-primary">Change Password</button>
                <button type="button" onClick={closePasswordPopup} className="btn btn-secondary">Close</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}